import { ITenantApiKey } from '@pepperhq/types-sdk';
import { isTenant, tenantsApi } from 'components/tenants/tenantsApi';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useAppSelector } from 'store/hooks';

export interface IApiKeyDetails {
    loading?: boolean;
    listLoading?: boolean;
    keys?: ITenantApiKey<string>[];
    loadingKeyId?: string;
}

export function useTenantApiKeys() {
    const { enqueueSnackbar } = useSnackbar();
    const { tenant } = useAppSelector(state => state.auth);
    const [apiKeyDetails, setApiKeyDetails] = React.useState<IApiKeyDetails>({ listLoading: true, keys: [] });

    const loadApiKeys = React.useCallback(() => {
        setApiKeyDetails(old => ({ ...old, listLoading: true }));
        tenantsApi
            .getTyped(tenant.tenantId, isTenant)
            .then(response => {
                setApiKeyDetails(old => ({ ...old, keys: response.body.apiKeys ?? [] }));
            })
            .catch(err => {
                enqueueSnackbar(`Unexpected error '${err.message}' while loading API keys`, {
                    variant: 'error'
                });
            })
            .finally(() => {
                setApiKeyDetails(old => ({ ...old, listLoading: false }));
            });
    }, [tenant.tenantId, enqueueSnackbar]);

    React.useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    const handleAddApiKey = React.useCallback(
        async (description: string, applicationIdPrefix?: string): Promise<ITenantApiKey<string> | null> => {
            setApiKeyDetails(old => ({ ...old, loading: true }));
            try {
                const res = await tenantsApi.createApiKey(
                    tenant.tenantId,
                    description,
                    applicationIdPrefix || undefined
                );
                if (res.error) {
                    enqueueSnackbar(res.error || 'Failed to create API key', { variant: 'error' });
                    loadApiKeys();
                    return null;
                }
                if (res.validationError) {
                    enqueueSnackbar(`API key created but got invalid response: ${res.validationError}`, {
                        variant: 'error'
                    });
                    loadApiKeys();
                    return null;
                }
                loadApiKeys();
                return res.body;
            } catch (err: any) {
                enqueueSnackbar(`Unexpected error '${err.message}' while creating API key`, {
                    variant: 'error'
                });
                return null;
            } finally {
                setApiKeyDetails(old => ({ ...old, loading: false }));
            }
        },
        [tenant.tenantId, enqueueSnackbar, loadApiKeys]
    );

    const handleToggleApiKey = React.useCallback(
        async (applicationId: string, isDisabled: boolean): Promise<void> => {
            setApiKeyDetails(old => ({ ...old, loadingKeyId: applicationId }));
            try {
                const res = await tenantsApi.updateApiKey(tenant.tenantId, applicationId, { isDisabled });
                if (res.error) {
                    enqueueSnackbar(res.error || 'Failed to update API key', { variant: 'error' });
                    loadApiKeys();

                    return;
                }
                if (res.validationError) {
                    enqueueSnackbar(`API key updated but got invalid response: ${res.validationError}`, {
                        variant: 'error'
                    });
                    loadApiKeys();
                    return;
                }
                setApiKeyDetails(old => ({ ...old, keys: res.body.apiKeys ?? [] }));
            } catch (err: any) {
                enqueueSnackbar(`Unexpected error '${err.message}' while updating API key`, {
                    variant: 'error'
                });
            } finally {
                setApiKeyDetails(old => ({ ...old, loadingKeyId: undefined }));
            }
        },
        [tenant.tenantId, enqueueSnackbar, loadApiKeys]
    );

    const handleDeleteApiKey = React.useCallback(
        async (applicationId: string): Promise<boolean> => {
            setApiKeyDetails(old => ({ ...old, loadingKeyId: applicationId }));
            try {
                const res = await tenantsApi.deleteApiKey(tenant.tenantId, applicationId);
                if (res.error) {
                    enqueueSnackbar(res.error || 'Failed to delete API key', { variant: 'error' });
                    loadApiKeys();
                    return true;
                }
                if (res.validationError) {
                    enqueueSnackbar(`API key deleted but got invalid response: ${res.validationError}`, {
                        variant: 'error'
                    });
                    loadApiKeys();
                    return true;
                }
                setApiKeyDetails(old => ({ ...old, keys: res.body.apiKeys ?? [] }));
                return true;
            } catch (err: any) {
                enqueueSnackbar(`Unexpected error '${err.message}' while deleting API key`, {
                    variant: 'error'
                });
                return false;
            } finally {
                setApiKeyDetails(old => ({ ...old, loadingKeyId: undefined }));
            }
        },
        [tenant.tenantId, enqueueSnackbar, loadApiKeys]
    );

    return { apiKeyDetails, handleAddApiKey, handleToggleApiKey, handleDeleteApiKey };
}
